import { useState } from 'react'
import { toast } from 'sonner'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@teach/ui'
import { api } from '@/store/api'
import { useAppDispatch } from '@/store/hooks'
import {
  idErrorMessage,
  useChangeEmailMutation,
  useGetIdProfileQuery,
  useVerifyEmailMutation,
} from '../profileApi'
import { EMAIL_RE, emailDialogHint, emailErrors } from '../emailFlow'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  current: string | null
}

/** Смена почты в два шага: новый адрес, потом код из письма. Аккаунт единый
 *  со студией и академией, поэтому почта меняется везде сразу. */
export const ChangeEmailDialog = ({ open, onOpenChange, current }: Props) => {
  const dispatch = useAppDispatch()
  const { refetch } = useGetIdProfileQuery()
  const [send, { isLoading: sending }] = useChangeEmailMutation()
  const [verify, { isLoading: verifying }] = useVerifyEmailMutation()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const m = useMessages(messages)

  const close = (next: boolean) => {
    if (!next) {
      setEmail('')
      setCode('')
      setSent(false)
      setError('')
    }
    onOpenChange(next)
  }

  const sendCode = async () => {
    const value = email.trim()
    if (!EMAIL_RE.test(value)) return setError(m.invalidEmail)
    if (value === current) return setError(m.sameEmail)
    try {
      await send({ email: value }).unwrap()
      setSent(true)
      setError('')
    } catch (e) {
      setError(idErrorMessage(e, m.sendCodeFailed, emailErrors()))
    }
  }

  const confirm = async () => {
    if (!code) return setError(m.enterCode)
    try {
      await verify({ code: Number(code) }).unwrap()
      await refetch()
      dispatch(api.util.invalidateTags(['Me']))
      toast.success(m.emailUpdated)
      close(false)
    } catch (e) {
      setError(idErrorMessage(e, m.verifyEmailFailed, emailErrors()))
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{m.emailTitle}</DialogTitle>
          <DialogDescription>{emailDialogHint(sent, email)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {sent ? (
            <Input
              autoFocus
              inputMode="numeric"
              value={code}
              placeholder={m.codePlaceholder}
              onChange={(e) => {
                setError('')
                setCode(e.target.value.replace(/\D/g, ''))
              }}
              onKeyDown={(e) => e.key === 'Enter' && void confirm()}
            />
          ) : (
            <Input
              autoFocus
              type="email"
              value={email}
              placeholder="name@example.com"
              onChange={(e) => {
                setError('')
                setEmail(e.target.value)
              }}
              onKeyDown={(e) => e.key === 'Enter' && void sendCode()}
            />
          )}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          {sent ? (
            <Button variant="secondary" onClick={() => void sendCode()} disabled={sending}>
              {m.resendCode}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => close(false)}>
              {m.cancel}
            </Button>
          )}
          <Button
            onClick={() => void (sent ? confirm() : sendCode())}
            disabled={sending || verifying}
          >
            {sent ? m.confirm : m.getCode}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
